import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
    DashIcon,
    MktgIcon,
    SettingsIcon,
    SupportIcon,
    WebsiteIcon,
    FansIcon,
    FansIconPath,
    DashboardIconPath,
    MarketingIconPath,
    WebsiteIconPath,
    SettingsIconPath,
    GiftIcon,
    GiftIconPath,
    SupportIconPath,
    LearnIcon,
    LearnIconPath,
    TeamIconPath,
    TeamIcon
} from "../svg-icons"
import MobileNavigation from "./MobileNavigation";
import DesktopNavigation from "./DesktopNavigation";
import PublicMobileNavigation from "./PublicMobileNavigation";
import PublicDesktopNavigation from "./PublicDesktopNavigation";
import Intercom from "helpers/Intercom";
import useBrand from 'Hooks/useBrand';
import useOrganization from 'Hooks/useOrganization';
import useScreen from "Hooks/useScreen";
import { useNavbar } from "Hooks/useNavbar";
import { useHistory, useLocation } from "react-router-dom";
import { clsx } from "modules/Utils";
import useMaestroChat from "Hooks/useMaestroChat";

export const showIntercom = () => {
    // @ts-ignore
    Intercom.open();
}

export const showNewMessageIntercom = () => {
    // @ts-ignore
    Intercom.showNewMessage();
}

export interface NavigationItem {
    name: string;
    smallName?: string;
    iconComponent?: React.ComponentType<{ color?: string; height?: string; width?: string }>;
    onClick?: (e: any) => void;
    href?: string;
    alternateHrefs?: string[];
    iconPath?: React.ReactElement,
    dropdownItems?: NavigationItem[]
    showOnMobileMainMenu: boolean;
    showOnDesktopMainMenu: boolean;
    showOnMobileSubMenu: boolean;
    showOnDesktopSubMenu: boolean;
    icon?: string;
    isPublic?: boolean;
    isAvailableAlways?: boolean; // Show in both public and authenticated views
    svgIconViewbox?: string;
    toolkitItems?: {
        name: string;
        href: string;
        icon: string;
        description: string;
        color: string;
    }[];
}

interface NavbarProps {
    className: string;
}

const Navbar = ({
    className,
}: NavbarProps) => {
    const history = useHistory();
    const location = useLocation();
    const { mobileView } = useScreen();
    const { isChatOpen } = useMaestroChat();
    const { shouldHideNavbar } = useNavbar();
    const { currentBrand, isPaidBrand } = useBrand()
    const { organization } = useOrganization();
    const hasOrganization = (organization && organization.name) || (currentBrand?.isFromAdminSearch && currentBrand?.organizationName);
    
    // Auth0 hooks for authentication
    const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();
    const isPublic = !isAuthenticated;

    const handleSignUp = () => {
        loginWithRedirect({
            authorizationParams: {
                screen_hint: 'signup'
            }
        });
    };

    const handleLogin = () => {
        loginWithRedirect();
    };

    const OLD_SITE_URL = 'https://site.symphonyos.co';

    let navigationItems: NavigationItem[] = [
        {
            name: "Maestro",
            href: "/",
            alternateHrefs: ["/assistant"],
            iconComponent: (props: { color?: string, height?: string, width?: string }) => <WebsiteIcon {...props} />,
            iconPath: <WebsiteIconPath />,
            icon: "maestroBadgeImage",
            svgIconViewbox: "0 0 18 18",
            showOnMobileMainMenu: true,
            showOnDesktopMainMenu: true,
            showOnMobileSubMenu: false,
            showOnDesktopSubMenu: false,
            isAvailableAlways: true, // Show in both public and authenticated views
        },
        {
            name: "Platform",
            icon: "marketing", // You might want to change this icon
            isPublic: true,
            showOnMobileMainMenu: true,
            showOnDesktopMainMenu: true,
            showOnMobileSubMenu: true,
            showOnDesktopSubMenu: true,
            dropdownItems: [
                // {
                //     name: "Meet Maestro, your AI agency",
                //     onClick: () => {
                //         // Link to be defined
                //         window.open('/', '_self'); // Placeholder, assuming it links to homepage or a specific section
                //     },
                //     icon: "learn", // You might want to change this icon
                //     isPublic: true,
                //     showOnMobileMainMenu: false,
                //     showOnDesktopMainMenu: false,
                //     showOnMobileSubMenu: true,
                //     showOnDesktopSubMenu: true,
                // },
                {
                    name: "Website Creation",
                    onClick: () => {
                        window.open(`${OLD_SITE_URL}/solutions/website-creation`, '_blank');
                    },
                    icon: "website", // You might want to change this icon
                    isPublic: true,
                    showOnMobileMainMenu: false,
                    showOnDesktopMainMenu: false,
                    showOnMobileSubMenu: true,
                    showOnDesktopSubMenu: true,
                },
                {
                    name: "Automated Ads",
                    onClick: () => {
                        window.open(`${OLD_SITE_URL}/solutions/automated-marketing`, '_blank');
                    },
                    icon: "marketing", // You might want to change this icon
                    isPublic: true,
                    showOnMobileMainMenu: false,
                    showOnDesktopMainMenu: false,
                    showOnMobileSubMenu: true,
                    showOnDesktopSubMenu: true,
                },
                {
                    name: "Cross-platform Analytics",
                    onClick: () => {
                        window.open(`${OLD_SITE_URL}/what-is-possible/identify-and-understand-your-audience`, '_blank');
                    },
                    icon: "dashboard", // You might want to change this icon
                    isPublic: true,
                    showOnMobileMainMenu: false,
                    showOnDesktopMainMenu: false,
                    showOnMobileSubMenu: true,
                    showOnDesktopSubMenu: true,
                },
                {
                    name: "Fan Growth & Insights",
                    onClick: () => {
                        window.open(`${OLD_SITE_URL}/solutions/fan-insights`, '_blank');
                    },
                    icon: "audiences", // You might want to change this icon
                    isPublic: true,
                    showOnMobileMainMenu: false,
                    showOnDesktopMainMenu: false,
                    showOnMobileSubMenu: true,
                    showOnDesktopSubMenu: true,
                },
            ]
        },
        {
            name: "About Us",
            onClick: () => {
                window.open(`${OLD_SITE_URL}/about`, '_self'); // Changed to _self to navigate in the same tab if it's part of the app
            },
            icon: "help-center",
            isPublic: true,
            showOnMobileMainMenu: true, // Show on mobile main menu
            showOnDesktopMainMenu: true,
            showOnMobileSubMenu: false,
            showOnDesktopSubMenu: false,
        },
        {
            name: "Learn",
            href: "/learn",
            alternateHrefs: [],
            iconComponent: (props: { color?: string, height?: string, width?: string }) => <LearnIcon {...props} />,
            iconPath: <LearnIconPath />,
            icon: "learn",
            isPublic: true,
            showOnMobileMainMenu: true, // Show on mobile main menu
            showOnDesktopMainMenu: true,
            showOnMobileSubMenu: true,
            showOnDesktopSubMenu: true,
            dropdownItems: [
                {
                    name: "Blog",
                    onClick: () => {
                        window.open(`${OLD_SITE_URL}/blog`, '_blank');
                    },
                    icon: "learn",
                    isPublic: true,
                    showOnMobileMainMenu: false,
                    showOnDesktopMainMenu: false,
                    showOnMobileSubMenu: true,
                    showOnDesktopSubMenu: true,
                },
                {
                    name: "Case Studies",
                    onClick: () => {
                        window.open(`${OLD_SITE_URL}/case-studies`, '_blank');
                    },
                    icon: "learn",
                    isPublic: true,
                    showOnMobileMainMenu: false,
                    showOnDesktopMainMenu: false,
                    showOnMobileSubMenu: true,
                    showOnDesktopSubMenu: true,
                },
                {
                    name: "Help Center",
                    onClick: () => {
                        window.open('https://support.symphonyos.co/en/', '_blank');
                    },
                    icon: "help-center",
                    isPublic: true,
                    showOnMobileMainMenu: false,
                    showOnDesktopMainMenu: false,
                    showOnMobileSubMenu: true,
                    showOnDesktopSubMenu: true,
                },
                {
                    name: "Video Walkthroughs",
                    onClick: () => {
                        window.open('https://www.youtube.com/@Symphony-OS', '_blank');
                    },
                    icon: "videos",
                    isPublic: true,
                    showOnMobileMainMenu: false,
                    showOnDesktopMainMenu: false,
                    showOnMobileSubMenu: true,
                    showOnDesktopSubMenu: true,
                },
            ]
        },
        // {
        //     name: "Pricing",
        //     href: "/pricing",
        //     icon: "marketing",
        //     isPublic: true,
        //     showOnMobileMainMenu: true, // Show on mobile main menu
        //     showOnDesktopMainMenu: true,
        //     showOnMobileSubMenu: false,
        //     showOnDesktopSubMenu: false,
        // },
        {
            name: "Get Started",
            onClick: handleSignUp,
            icon: "user",
            isPublic: true,
            showOnMobileMainMenu: false,
            showOnDesktopMainMenu: true,
            showOnMobileSubMenu: false,
            showOnDesktopSubMenu: false,
        },
        {
            name: "Log In",
            onClick: handleLogin,
            icon: "audiences",
            isPublic: true,
            showOnMobileMainMenu: true,
            showOnDesktopMainMenu: true,
            showOnMobileSubMenu: false,
            showOnDesktopSubMenu: false,
        },
        {
            name: "Marketing",
            href: "/marketing",
            alternateHrefs: ["/marketing"],
            iconComponent: (props: { color?: string, height?: string, width?: string }) => <MktgIcon {...props} />,
            iconPath: <MarketingIconPath />,
            icon: "marketing",
            showOnMobileMainMenu: false,
            showOnDesktopMainMenu: true,
            showOnMobileSubMenu: false,
            showOnDesktopSubMenu: false,
        },
        {
            name: "Website",
            href: "/website",
            alternateHrefs: [],
            iconComponent: (props: { color?: string, height?: string, width?: string }) => <WebsiteIcon {...props} />,
            iconPath: <WebsiteIconPath />,
            icon: "website",
            showOnMobileMainMenu: false,
            showOnDesktopMainMenu: true,
            showOnMobileSubMenu: false,
            showOnDesktopSubMenu: false,
        },
        {
            name: "Dashboard",
            href: "/dashboard",
            alternateHrefs: [],
            iconComponent: (props: { color?: string, height?: string, width?: string }) => <DashIcon {...props} />,
            iconPath: <DashboardIconPath />,
            icon: "dashboard",
            showOnMobileMainMenu: false,
            showOnDesktopMainMenu: true,
            showOnMobileSubMenu: false,
            showOnDesktopSubMenu: false,
        },
        {
            name: "Fans",
            href: "/fans",
            alternateHrefs: [],
            iconComponent: (props: { color?: string, height?: string, width?: string }) => <FansIcon {...props} />,
            iconPath: <FansIconPath />,
            icon: "audiences",
            showOnMobileMainMenu: false,
            showOnDesktopMainMenu: true,
            showOnMobileSubMenu: false,
            showOnDesktopSubMenu: false,
        },
        {
            name: "Team",
            href: "/team",
            alternateHrefs: [],
            iconComponent: (props: { color?: string, height?: string, width?: string }) => <TeamIcon {...props} />,
            iconPath: <TeamIconPath />,
            icon: "home",
            showOnMobileMainMenu: false,
            showOnDesktopMainMenu: true,
            showOnMobileSubMenu: true,
            showOnDesktopSubMenu: false
        },
        // Add new Toolkit navigation item
        {
            name: "Toolkit",
            icon: "wand",
            showOnMobileMainMenu: true,
            showOnDesktopMainMenu: false,
            showOnMobileSubMenu: false,
            showOnDesktopSubMenu: false,
            // This will be handled as a special case in MobileNavigation
            toolkitItems: [
                {
                    name: "Ad Campaigns",
                    href: "/marketing",
                    icon: "marketing",
                    description: "Create & manage ads",
                    color: "#FFEDDB" // campaignOrange
                },
                {
                    name: "Websites",
                    href: "/website", 
                    icon: "website",
                    description: "Build & customize",
                    color: "#D7F6D7" // campaignGreen
                },
                {
                    name: "Audience Analytics",
                    href: "/dashboard",
                    icon: "dashboard", 
                    description: "Performance insights",
                    color: "#DBFDFC" // campaignBlue
                },
                {
                    name: "CRM (Fans)",
                    href: "/fans",
                    icon: "audiences",
                    description: "Grow your fanbase", 
                    color: "#F1E0FF" // campaignPurple
                }
            ]
        },
        // only shown on mobile - additional options
        {
            name: "Settings",
            href: '/settings',
            iconComponent: (props: { color?: string, height?: string, width?: string }) => <SettingsIcon {...props} />,
            iconPath: <SettingsIconPath />,
            icon: "gear",
            showOnMobileMainMenu: false,
            showOnDesktopMainMenu: false,
            showOnMobileSubMenu: false,
            showOnDesktopSubMenu: false,
        },
        {
            name: "Refer & Earn",
            smallName: "Refer",
            href: '/refer',
            alternateHrefs: [],
            iconComponent: (props: {}) => <GiftIcon {...props} />,
            iconPath: <GiftIconPath />,
            icon: "gift",
            showOnMobileMainMenu: false,
            showOnDesktopMainMenu: false,
            showOnMobileSubMenu: false,
            showOnDesktopSubMenu: false,
        },
        {
            name: "Contact Support",
            iconComponent: (props: { color?: string, height?: string, width?: string }) => <SupportIcon {...props} />,
            iconPath: <SupportIconPath />,
            icon: "support",
            onClick: (e: any) => {
                e.preventDefault()
                showIntercom()
            },
            showOnMobileMainMenu: false,
            showOnDesktopMainMenu: false,
            showOnMobileSubMenu: false,
            showOnDesktopSubMenu: false,
        },
    ]

    /** Remove Fans tab if specified in brand settings */
    if (currentBrand?.hideFansTab) {
        navigationItems = navigationItems.filter((navItem: NavigationItem) => navItem.name !== "Fans");
    }

    /** Update Team visibility based on organization status */
    // if (hasOrganization) {
    //     navigationItems = navigationItems.map(item =>
    //         item.name === "Team"
    //             ? { ...item, showOnDesktopMainMenu: true }
    //             : item
    //     );
    // }

    const hideNavbar = shouldHideNavbar(location.pathname, organization, currentBrand);

    if (!isPublic && hideNavbar) {
        return null;
    }

    if (isPublic) {
        if(isChatOpen) {
            return null;
        }
        if (mobileView) {
            return (
                <PublicMobileNavigation
                    className={clsx(className, 'publicMobileNavigationFromNavbar')}
                    navigationItems={navigationItems}
                />
            )
        }

        return (
            <PublicDesktopNavigation
                className={clsx(className, 'publicDesktopNavigationFromNavbar')}
                navigationItems={navigationItems}
            />
        )
    }

    if (mobileView) {
        return (
            <MobileNavigation
                className={clsx(className, 'mobileNavigationFromNavbar')}
                navigationItems={navigationItems}
                isPublic={isPublic}
            />
        )
    }

    return (
        <DesktopNavigation
            className={clsx(className, 'desktopNavigationFromNavbar')}
            navigationItems={navigationItems}
            isPublic={isPublic}
        />
    )
}

export default Navbar
